import { IconButton, Typography } from '@material-ui/core';
import { Skeleton } from '@material-ui/lab';
import { Close, InfoRounded } from '@material-ui/icons';
import { parseDate } from '@superdispatch/dates';
import {
  FormikCheckboxField,
  FormikDateField,
  FormikTextField,
  useFormikEnhanced,
} from '@superdispatch/forms';
import {
  Column,
  Columns,
  DrawerContent,
  DrawerTitle,
  ExitTransitionPlaceholder,
  Stack,
  useResponsiveValue,
} from '@superdispatch/ui';
import { Alert, AnchorButton, Box } from '@superdispatch/ui-lab';
import { DateTime } from 'luxon';
import styled from 'styled-components';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { getAuthToken } from 'shared/auth/AuthToken';
import { FormikDrawer, FormikDrawerContent } from 'shared/form/FormikDrawer';
import { toFormikValidationSchema } from 'shared/form/FormSchema';
import { startIntercomSurvey } from 'shared/helpers/Intercom';
import { formatLoadPayment } from 'shared/helpers/PaymentHelpers';
import { LocalStore, useLocalStore } from 'shared/helpers/Store';
import { useDispatcherProfile } from 'shared/modules/dispatcher/DispatcherAPI';
import { ContactDTO } from '../../contacts/data/ContactDTO';
import { sendInvoiceSchema, zodBolTemplate } from '../data/LoadActionsDTO';
import { BolTemplateRadioGroupField } from './BolTemplateRadioGroupField';
import {
  CombinedLoadDTO,
  CustomerDTO,
  formatLoadStage,
  LoadDTO,
  loadVenueSchema,
} from '../data/LoadDTO';
import { trackLoadsEvent } from '../data/LoadsAnalytics';
import { useAccountingInfo, useLoadDetails } from '../data/LoadsAPI';
import {
  getApplicableBolTemplates,
  useCarrierSettings,
} from 'shared/settings/CarrierSettingsAPI';
import { useFlag } from 'shared/settings/FeatureToggles';
import { FormikVenueAutofill } from './FormikVenueAutofill';
import { LoadEmailSelectionField } from './LoadEmailSelectionField';
import { LoadSelectionField } from './LoadSelectionField';
import { LoadSendInvoiceAttachments } from './LoadSendInvoiceAttachments';
import { LoadSendInvoiceDrawerActions } from './LoadSendInvoiceDrawerActions';
import { useSuperPayRequested } from './LoadSendInvoiceRequestPayment';
import { LoadSendInvoiceStatus } from './LoadSendInvoiceStatus';
import { useSubhaulLoad } from '../view/useSubhaulLoad';

const LOAD_SEND_INVOICE_COUNT_STORAGE_KEY = 'load_send_invoice_count';
const newSendInvoiceIntercomSurveyId =
  import.meta.env.VITE_APP_TARGET === 'production' ? '34538606' : '34557398';
const BANNER_QUICKBOOK_SEEN_KEY = 'quickbook.has_seen_banner';
const SIGNED_UP_AFTER = '2024-02-01T00:00:00Z';

interface LoadSendInvoiceDrawerProps {
  onClose: () => void;
  isLoading?: boolean;
  stage?: string;
  loadGUID?: string;
  loadNumber?: string;
  inspectionType?: string;
  isCreatedByBroker?: boolean;
}

function useHasSeenQuickBooksAlert() {
  const quickBooksBannerSeen = useLocalStore(BANNER_QUICKBOOK_SEEN_KEY, null);
  const { data: dispatcher } = useDispatcherProfile();

  const createdAtDate = parseDate(dispatcher?.created_at as string, {
    format: 'DateISO',
  });

  const setShowQuickBooksAlert = useCallback(() => {
    LocalStore.set(BANNER_QUICKBOOK_SEEN_KEY, 'true');
  }, []);

  return {
    setShowQuickBooksAlert,
    shouldShowQuickBooksBanner:
      quickBooksBannerSeen !== 'true' &&
      createdAtDate > DateTime.fromISO(SIGNED_UP_AFTER),
  };
}

const SkeletonContainer = styled('div')`
  width: 500px;
`;

function SendInvoiceDrawerSkeleton({ onClose }: { onClose: () => void }) {
  return (
    <SkeletonContainer>
      <DrawerTitle
        title="Send Invoice"
        endAction={
          <IconButton edge="end" onClick={onClose}>
            <Close aria-label="close" />
          </IconButton>
        }
      />
      <DrawerContent>
        <Stack space="large">
          <Stack space="small">
            <Skeleton variant="rect" height={56} />
            <Columns space="xsmall">
              <Column width="1/2">
                <Skeleton variant="rect" height={56} />
              </Column>
              <Column width="1/2">
                <Skeleton variant="rect" height={56} />
              </Column>
            </Columns>
          </Stack>
          <Stack space="small">
            <Skeleton variant="text" width="30%" height={24} />
            <Skeleton variant="rect" height={80} />
          </Stack>
          <Stack space="small">
            <Skeleton variant="text" width="40%" height={24} />
            <Skeleton variant="rect" height={32} />
            <Skeleton variant="rect" height={32} />
            <Skeleton variant="rect" height={32} />
          </Stack>
        </Stack>
      </DrawerContent>
    </SkeletonContainer>
  );
}

export function LoadSendInvoiceDrawer({
  stage,
  onClose,
  loadGUID,
  isLoading,
  loadNumber,
  isCreatedByBroker,
  inspectionType,
}: LoadSendInvoiceDrawerProps) {
  const { isSubhauled } = useSubhaulLoad();

  return (
    <FormikDrawer
      open={!!loadGUID || !!isLoading}
      onClose={onClose}
      drawerProps={{ disableEnforceFocus: true }}
      enableConfirmationOnClose={!isSubhauled}
    >
      <ExitTransitionPlaceholder in={!!loadGUID || !!isLoading}>
        {isLoading ? (
          <SendInvoiceDrawerSkeleton onClose={onClose} />
        ) : (
          <SendInvoiceDrawerContent
            stage={stage}
            onClose={onClose}
            loadGUID={loadGUID}
            loadNumber={loadNumber}
            isCreatedByBroker={isCreatedByBroker}
            inspectionType={inspectionType}
          />
        )}
      </ExitTransitionPlaceholder>
    </FormikDrawer>
  );
}

type SendInvoiceDrawerContentProps = LoadSendInvoiceDrawerProps;

function handleEmails(customer?: CustomerDTO) {
  if (!customer?.email) {
    return [];
  }

  return customer.email.split(',').map((email) => ({ email: email.trim() }));
}

function SendInvoiceDrawerContent({
  onClose,
  stage,
  loadGUID,
  loadNumber,
  inspectionType,
  isCreatedByBroker,
}: SendInvoiceDrawerContentProps) {
  const { isSubhauled } = useSubhaulLoad();
  const { data: settings } = useCarrierSettings();
  const isCleanBolEnabled = useFlag('ctms_clean_bol');
  const { data: loadDetails } = useLoadDetails(
    loadGUID,
    { isSubhauled },
    { enabled: !!loadGUID && !inspectionType },
  );
  const loadInspectionType = inspectionType ?? loadDetails?.inspection_type;
  const applicableBolTemplates = useMemo(
    () =>
      getApplicableBolTemplates(settings?.bol_templates, loadInspectionType),
    [settings?.bol_templates, loadInspectionType],
  );
  const shouldShowBolTemplateField = useMemo(() => {
    if (isCleanBolEnabled) {
      return applicableBolTemplates.length > 1;
    }

    return loadInspectionType !== 'aiag';
  }, [isCleanBolEnabled, applicableBolTemplates, loadInspectionType]);
  const defaultBolTemplate = useMemo(() => {
    if (isCleanBolEnabled && applicableBolTemplates.length === 1) {
      return applicableBolTemplates[0]?.key ?? 'default';
    }

    if (isCleanBolEnabled && applicableBolTemplates.length > 1) {
      return (
        applicableBolTemplates.find(({ key }) => key === 'default')?.key ??
        applicableBolTemplates[0]?.key ??
        'default'
      );
    }

    return 'default';
  }, [isCleanBolEnabled, applicableBolTemplates]);
  const sendInvoiceFormSchema = useMemo(() => {
    if (!isCleanBolEnabled || applicableBolTemplates.length <= 1) {
      return sendInvoiceSchema;
    }

    // `safeExtend` because `sendInvoiceSchema` carries the recipient-list check.
    return sendInvoiceSchema.safeExtend({
      bol_template: zodBolTemplate(
        applicableBolTemplates.map(({ key }) => key),
      ),
    });
  }, [isCleanBolEnabled, applicableBolTemplates]);

  const validationSchema = useMemo(
    () => toFormikValidationSchema(sendInvoiceFormSchema),
    [sendInvoiceFormSchema],
  );
  const screen = useResponsiveValue('mobile', 'tablet', 'desktop');
  const isMobile = screen === 'mobile' || screen === 'tablet';
  const loadSendInvoiceCount = useLocalStore(
    LOAD_SEND_INVOICE_COUNT_STORAGE_KEY,
    '1',
  );
  const [isSubmitedForm, setSubmitForm] = useState(false);
  const [initialOrder, setInitialOrder] = useState<CombinedLoadDTO>();

  const authToken = getAuthToken();
  const integrationsURL = `/apps/integrations?token=${authToken}?utm_medium=Send Invoice`;

  const { data: accountInfo } = useAccountingInfo({
    order_guid: loadGUID,
    skip_customer_name: true,
  });

  const paymentMethod: string = accountInfo?.order?.payment?.method ?? '';

  const { isEnableSuperPayRequestCheckBox } = useSuperPayRequested(
    paymentMethod,
    !!isCreatedByBroker,
  );

  const { setShowQuickBooksAlert, shouldShowQuickBooksBanner } =
    useHasSeenQuickBooksAlert();

  const formik = useFormikEnhanced({
    validationSchema,
    enableReinitialize: true,
    initialValues: {
      customer: { name: accountInfo?.customer.name },
      invoice_id: accountInfo?.invoice_number,
      invoice_date: accountInfo?.invoice_date,
      orders: initialOrder
        ? [initialOrder]
        : accountInfo?.order
          ? [{ ...accountInfo.order, attachments: accountInfo.attachments }]
          : [],
      email: !!accountInfo?.customer.email,
      emails: handleEmails(accountInfo?.customer as CustomerDTO),
      bol_template: defaultBolTemplate,
      factor_this_invoice: false,
      qb_online: accountInfo?.qb_online.connected,
      qb_desktop: accountInfo?.qb_desktop.connected,
      attachments: [],
      is_carrier_requested_superpay: isEnableSuperPayRequestCheckBox,
    },
    onSubmit: () => {
      setSubmitForm(true);
    },
    onSubmitSuccess: (_, value) => {
      const invoiceType =
        (value.email && value.qb_online) ||
        (value.email && value.qb_desktop) ||
        (value.qb_desktop && value.qb_online)
          ? 'combined'
          : 'single';
      let sendingMethod = [];
      if (value.email) sendingMethod.push('email');
      if (value.qb_online) sendingMethod.push('qbo');
      if (value.qb_desktop) sendingMethod.push('qbd');

      const invoiceSentCount = Number(loadSendInvoiceCount);

      if (invoiceSentCount < 3) {
        LocalStore.set(
          LOAD_SEND_INVOICE_COUNT_STORAGE_KEY,
          String(invoiceSentCount + 1),
        );
      } else if (invoiceSentCount === 3) {
        startIntercomSurvey(newSendInvoiceIntercomSurveyId);

        // Setting the count to 4 so that the survey is not shown again
        LocalStore.set(LOAD_SEND_INVOICE_COUNT_STORAGE_KEY, '4');
      }

      trackLoadsEvent({
        name: 'Carrier Sent Invoice',
        bol_template: value.bol_template,
        factor: value.factor_this_invoice,
        invoice_type: invoiceType,
        is_attachment_added: !!value.attachments.length,
        payment_method: formatLoadPayment(paymentMethod),
        sending_method: sendingMethod,
        carrier_requested_payment_via_superpay:
          !!value.is_carrier_requested_superpay,
        utm_medium: formatLoadStage(stage as LoadDTO['stage']),
      });
    },
  });
  const { values, setValues } = formik;

  const isSubmitDisabled =
    !values.qb_online && !values.qb_desktop && !values.email;

  const handleBack = () => {
    setSubmitForm(false);
  };

  const handleSelectCustomer = (contact: ContactDTO) => {
    if (!contact.email) {
      return;
    }

    const emails = contact.email
      .split(',')
      .map((item) => item.replace(' ', ''));

    void setValues((value) => ({
      ...value,
      email: true,
      emails: [
        ...value.emails,
        ...emails.map((email) => ({
          email,
          guid: contact.guid,
          name: contact.name,
          fax: '',
        })),
      ],
    }));
  };

  useEffect(() => {
    trackLoadsEvent({
      name: 'Carrier Opened Send Invoice Drawer',
      carrier_requested_payment_via_superpay: !!isEnableSuperPayRequestCheckBox,
      utm_medium: formatLoadStage(stage as LoadDTO['stage']),
    });
    // update tracking event when superpay is changed
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEnableSuperPayRequestCheckBox]);

  const isQBConnected =
    accountInfo?.qb_online.connected || accountInfo?.qb_desktop.connected;

  const width = isMobile ? '100%' : '500px';

  if (!isSubmitedForm) {
    return (
      <FormikDrawerContent
        title="Send Invoice"
        formik={formik}
        onClose={onClose}
        minWidth={width}
        maxWidth={width}
        disableAutoClose={isSubhauled}
        actions={
          <LoadSendInvoiceDrawerActions
            submitDisabled={isSubmitDisabled}
            orders={values.orders}
            paymentMethod={paymentMethod}
            isCreatedByBroker={isCreatedByBroker}
          />
        }
      >
        <Stack space="large">
          {!isQBConnected && shouldShowQuickBooksBanner && (
            <Alert
              icon={<InfoRounded />}
              onClose={() => {
                setShowQuickBooksAlert();
              }}
              severity="info"
            >
              <Box>
                <Stack>
                  <Typography variant="h5">Do you use QuickBooks?</Typography>
                  <Typography>
                    Super Dispatch offers a seamless integration with QuickBooks
                    Online to help you streamline invoicing and payments.
                  </Typography>

                  <AnchorButton
                    size="small"
                    variant="neutral"
                    target="_blank"
                    href={integrationsURL}
                    onClick={() => {
                      setShowQuickBooksAlert();
                      onClose();
                    }}
                  >
                    Learn more
                  </AnchorButton>
                </Stack>
              </Box>
            </Alert>
          )}
          <Stack space="small">
            <FormikVenueAutofill
              fullWidth={true}
              label="Customer"
              name="customer"
              schema={loadVenueSchema}
              onSelect={handleSelectCustomer}
              inputProps={{ maxLength: 100 }}
              disabled={isCreatedByBroker && !!accountInfo?.customer.name}
            />
            <Columns space="xsmall">
              <Column width="1/2">
                <FormikTextField
                  fullWidth={true}
                  name="invoice_id"
                  label="Invoice ID"
                />
              </Column>
              <Column width="1/2">
                <FormikDateField
                  fullWidth={true}
                  variant="Date"
                  label="Invoice Date"
                  name="invoice_date"
                  enableClearable={true}
                />
              </Column>
            </Columns>
          </Stack>
          <LoadSelectionField
            guid={loadGUID}
            loadNumber={loadNumber}
            name="orders"
            onUpdateInitialValue={(order) => {
              setInitialOrder(order);
            }}
          />

          {accountInfo?.is_factoring_switched_on && (
            <Box>
              <FormikCheckboxField
                name="factor_this_invoice"
                label="Factor Invoice"
              />
              <Box paddingLeft="large">
                <Typography color="textSecondary">
                  Notice of Assignment sticker is added to invoice.
                </Typography>
              </Box>
            </Box>
          )}
          <Stack space="small">
            <Typography variant="h4">Sending Method</Typography>
            <Box>
              <FormikCheckboxField name="email" label="Email" />
              <Box paddingLeft="large">
                <LoadEmailSelectionField name="emails" />
              </Box>
            </Box>
            {shouldShowBolTemplateField && (
              <Box paddingLeft="large">
                <Typography variant="h5" color="textSecondary">
                  BOL Template
                </Typography>
                <BolTemplateRadioGroupField
                  name="bol_template"
                  isCleanBolEnabled={isCleanBolEnabled}
                  applicableBolTemplates={applicableBolTemplates}
                />
              </Box>
            )}

            {accountInfo?.qb_online.connected && (
              <Box>
                <FormikCheckboxField
                  name="qb_online"
                  label="QuickBooks Online"
                />
                {accountInfo.qb_online.message && (
                  <Box paddingLeft="large">
                    <Typography color="textSecondary">
                      {accountInfo.qb_online.message}
                    </Typography>
                  </Box>
                )}
              </Box>
            )}

            {accountInfo?.qb_desktop.connected && (
              <Box>
                <FormikCheckboxField
                  name="qb_desktop"
                  label="QuickBooks Desktop"
                />

                {accountInfo.qb_desktop.message && (
                  <Box paddingLeft="large">
                    <Typography color="textSecondary">
                      {accountInfo.qb_desktop.message}
                    </Typography>
                  </Box>
                )}
              </Box>
            )}
          </Stack>

          {accountInfo?.attachments && accountInfo.attachments.length > 0 && (
            <LoadSendInvoiceAttachments name="attachments" />
          )}
        </Stack>
      </FormikDrawerContent>
    );
  }

  return (
    <FormikDrawerContent
      title="Send Invoice"
      formik={formik}
      onClose={onClose}
      enableBack={true}
      onBack={handleBack}
      disableNavigationBlock={isSubhauled}
      minWidth="500px"
      maxWidth="500px"
    >
      <LoadSendInvoiceStatus onClose={onClose} />
    </FormikDrawerContent>
  );
}
